import React from 'react';
import { useSAData } from './useSAData';
import { SAKpis } from './SAKpis';
import { Loading } from './Loading';
import { F } from './formulas';
import { AnalyticsTable } from '@/components/po-grn/AnalyticsTable';
import { STORE_COLS } from './columns';
import { dmy, inr, pct } from '@/components/po-grn/format';

const KPIS = [
  { key: 'stores_with_sales', label: 'Stores with Sales' },
  { key: 'new_stores', label: 'New Buying Stores' },
  { key: 'repeat_stores', label: 'Repeat Buying Stores' },
  { key: 'inactive_stores', label: 'Inactive Stores', warn: true },
  { key: 'net_sales_per_store', label: 'Net Sales per Store', type: 'money' },
  { key: 'aov', label: 'Average Document Value', type: 'money' },
  { key: 'avg_days_between', label: 'Avg Days Between Orders', type: 'days' },
  { key: 'growth_pct', label: 'Store Sales Growth %', type: 'pct', note: (d) => `${d.no_comparison_stores} store(s) without valid comparison` },
  { key: 'margin_per_store', label: 'Current Margin Value per Store', type: 'money', note: `${F.currentValue} ÷ stores` },
  { key: 'prm_per_store', label: 'Outward Margin Value per Store', type: 'money', note: `${F.outwardValue} ÷ stores` },
  { key: 'neg_margin_stores', label: 'Stores with Negative Current Margin', warn: true },
  { key: 'dq_stores', label: 'Stores with Missing/Invalid Pricing', warn: true, drill: { patch: { flag: ['sales', 'missing_pricing'] } } },
];

const RANKS = [
  ['top_stores', 'Top Stores', 'sales', inr], ['fastest_growing', 'Fastest-Growing', 'growth_pct', pct],
  ['declining', 'Declining', 'growth_pct', pct], ['low_margin', 'Lowest Reported Margin', 'margin_pct', pct],
  ['highest_prm', 'Highest Potential MRP Benefit', 'prm', inr],
];

const Ranking = ({ title, rows, valueKey, fmt, onPick, testId }) => (
  <div className="rounded-xl border border-orange-200/70 bg-white p-3" data-testid={testId}>
    <div className="text-xs font-semibold text-[#3b2416] mb-2">{title}</div>
    {!rows?.length && <div className="text-[11px] text-stone-400 py-3">No stores qualify</div>}
    {(rows || []).map((s, i) => (
      <button key={s.franchise_id} type="button" onClick={() => onPick(s)} data-testid={`${testId}-item`}
        className="w-full flex items-center justify-between gap-2 text-[11px] py-1 px-1 rounded hover:bg-orange-50">
        <span className="truncate text-stone-700"><span className="text-stone-400 mr-1">{i + 1}.</span>{s.franchise_name}</span>
        <span className={`tabular-nums font-semibold ${s[valueKey] < 0 ? 'text-red-600' : 'text-[#3b2416]'}`}>{fmt(s[valueKey])}</span>
      </button>
    ))}
  </div>
);

export const StoresTab = ({ params, onDrill }) => {
  const { data, loading } = useSAData('/stores', params);
  const pick = (s) => onDrill({ patch: { franchise_ids: [s.franchise_id], flag: ['sales'] } }, `Store ${s.franchise_name}`);
  const p = data?.periods;
  return (
    <div className="space-y-4 relative">
      <Loading on={loading} />
      {p && (
        <div className="text-[11px] text-stone-600" data-testid="sa-store-periods">
          Current period {dmy(p.cur_from)} → {dmy(p.cur_to)} vs previous {dmy(p.prev_from)} → {dmy(p.prev_to)} ({p.days} days each) ·
          Inactive = no invoice or DC sale in {data.inactivity_days} days · Grouped by Franchise ID · Current Margin % = {F.current} · Outward Margin % = {F.outward}
        </div>
      )}
      <SAKpis items={KPIS} data={data?.kpis} onDrill={onDrill} testPrefix="sa-stores" />
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {RANKS.map(([k, title, vk, fmt]) => (
          <Ranking key={k} title={title} rows={data?.rankings?.[k]} valueKey={vk} fmt={fmt} onPick={pick} testId={`sa-rank-${k}`} />
        ))}
      </div>
      <AnalyticsTable columns={STORE_COLS} rows={data?.stores || []} testId="sa-store-table" rowKey={(r) => r.franchise_id} />
    </div>
  );
};
